import React from 'react';
import { useSelector } from 'react-redux';

import { useTranslation, useEnvironment, useBi } from '@wix/yoshi-flow-editor';

import { useCurrentStateAndParams, useRouter } from '@wix/tpa-router/react';
import uuid from 'uuid/v4';

import {
  groupsHeaderButtonClick,
  groupsNotificationShownSrc111Evid510,
} from '@wix/bi-logger-groups-data/v2';

import { LocationType } from '@wix/ambassador-socialgroups-v1-group-members-event/types';

import { selectGroupIdBySlug } from 'store/selectors';

import { useDidUpdate } from 'common/hooks';
import { useController } from 'common/context/controller';

import { EventForm, IEventForm } from '../common/EventForm';

import {
  getDeviceTimeZoneId,
  getInitialValues,
  toEventImage,
} from '../helpers';
import { selectCreationStatus } from '../selectors';
import { EventPage, EventPageHeader } from '../common/EventPage';
import { EventDrawer } from '../common/EventDrawer';

export function CreateEventPage() {
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const bi = useBi();

  const { events$ } = useController();
  const { params } = useCurrentStateAndParams();
  const status = useSelector(selectCreationStatus);
  const groupId = useSelector(selectGroupIdBySlug(params.slug));

  const [isBusy, setIsBusy] = React.useState(false);

  const initialValues = getInitialValues();

  const router = useRouter();

  const isLoading = isBusy || status.loading;

  useDidUpdate(() => {
    // event has been created
    if (!status.loading && !status.error) {
      bi.report(
        groupsNotificationShownSrc111Evid510({
          type: 'success',
          name: 'event_created',
          group_id: groupId,
        }),
      );
      events$.fetch(groupId);
      router.go('group.events.list');
    }
  }, [status.loading]);

  if (isMobile) {
    return (
      <EventDrawer
        data-hook="create-event-page"
        isOpen
        onClose={() => {
          router.go('group.events.list');
        }}
        isLoading={isLoading}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onBusyStateChange={setIsBusy}
        title={t('groups-web.create-event-page.header.title')}
        subtitle={t('groups-web.create-event-page.header.subtitle')}
        actions={{
          primary: {
            label: t('groups-web.create-event-drawer.submit'),
          },
          secondary: {
            label: t('groups-web.create-event-drawer.cancel'),
            onClick: reportCancelClick,
          },
        }}
      />
    );
  }

  return (
    <EventPage
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onBusyStateChange={setIsBusy}
      data-hook="create-event-page"
    >
      <>
        <EventPageHeader
          title={t('groups-web.create-event-page.header.title')}
          subtitle={t('groups-web.create-event-page.header.subtitle')}
          isLoading={isLoading}
          actions={{
            primary: {
              label: t('groups-web.create-event-page.header.create.label'),
            },
            secondary: {
              label: t('groups-web.create-event-page.header.cancel.label'),
              onClick: reportCancelClick,
            },
          }}
        />

        <EventForm />
      </>
    </EventPage>
  );

  function handleSubmit(values: IEventForm) {
    const id = uuid();

    bi.report(
      groupsHeaderButtonClick({
        button_name: 'create_event',
        page_name: 'events_livesite',
        details: JSON.stringify({
          event_id: id,
          has_photo: Boolean(values.cover),
        }),
      }),
    );

    events$.create({
      id,
      groupId,
      title: values.name,
      shortDescription: values.teaser,
      location: {
        locationTbd: true,
        name: values.location,
        type: LocationType.UNKNOWN_LOCATION,
      },
      dateAndTimeSettings: {
        dateAndTimeTbd: false,
        startDate: new Date(values.startDate).toISOString(),
        endDate: new Date(values.endDate).toISOString(),
        timeZone: getDeviceTimeZoneId(),
      },
      image: toEventImage(values.cover),
    });
  }

  function reportCancelClick() {
    bi.report(
      groupsHeaderButtonClick({
        button_name: 'cancel',
        page_name: 'events_livesite',
      }),
    );
  }
}

CreateEventPage.displayName = 'CreateEventPage';
